import React, { useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import LeadershipSection from "../components/about/LeadershipSection";
import ManufacturingExcellence from "../components/about/ManufacturingExcellence";
import InsideLiftileGallery from "../components/about/InsideLiftileGallery";
import QualityWithoutCompromise from "../components/about/QualityWithoutCompromise";
import TrustedComponentsAbout from "../components/about/TrustedComponentsAbout";
import EngineeringPhilosophy from "../components/about/EngineeringPhilosophy";
import MissionVisionSection from "../components/about/MissionVisionSection";
import IndiaPresenceSection from "../components/about/IndiaPresenceSection";
import BuildingTomorrowSection from "../components/about/BuildingTomorrowSection";
import LiftilePromiseSection from "../components/about/LiftilePromiseSection";
import AboutFAQSection from "../components/about/AboutFAQSection";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Quote } from "lucide-react";
import Seo, { buildBreadcrumb } from "../components/Seo";
import { PAGE_SEO } from "../data/seo";

const HERO_FACTORY_IMG = "/about-header-factory.png";
const ABOUT_FACTORY_IMG = "/about-section-factory.png";

// Subtle entrance variant reused across blocks for consistency.
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: "easeOut" } },
};

/* =====================================================================
   HERO — full-screen 100vh, factory photograph background with
   Ken-Burns zoom, 70 % black cinematic overlay, orange ambient glow
   and a subtle scroll-down indicator.
   ===================================================================== */
function AboutHero() {
  return (
    <section
      data-testid="about-hero"
      className="relative w-full overflow-hidden bg-liftile-black"
      style={{ height: "100vh", minHeight: "640px" }}
    >
      {/* Background image with slow Ken-Burns animation */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${HERO_FACTORY_IMG})`,
          animation: "about-kenburns 28s ease-in-out infinite alternate",
        }}
      />

      {/* 70 % cinematic black overlay (top→bottom for legibility) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.88) 100%)",
        }}
      />

      {/* Soft orange ambient glow — bottom-left + top-right */}
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[640px] h-[640px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.22) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.14) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Centered content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="inline-flex items-center gap-3 mb-6"
        >
          <span className="block w-10 h-px bg-liftile-orange" />
          <span className="font-heading text-[10px] md:text-xs uppercase tracking-[0.32em] text-liftile-orange">
            About LIFTILE
          </span>
          <span className="block w-10 h-px bg-liftile-orange" />
        </motion.div>

        <motion.h1
          data-testid="about-hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
          className="font-display uppercase text-liftile-cream leading-[0.92] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl"
        >
          Engineering Trust.
          <br />
          <span className="text-liftile-orange">Building the Future.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
          className="font-body text-liftile-cream/80 mt-7 text-base md:text-lg leading-relaxed max-w-3xl"
        >
          From precision manufacturing to dependable after-sales support, LIFTILE
          is committed to delivering premium construction machinery engineered
          for safety, reliability and long-term performance.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45, ease: "easeOut" }}
          className="flex flex-col sm:flex-row gap-4 mt-10"
        >
          <Link
            to="/products"
            data-testid="about-hero-cta-products"
            className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-12px_rgba(255,107,26,0.85)]"
          >
            Explore Products
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="about-hero-cta-contact"
            className="group inline-flex items-center justify-center gap-3 border border-white/40 bg-white/[0.04] backdrop-blur-sm text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm transition-all duration-400 hover:bg-white hover:text-liftile-matte-black hover:border-white hover:-translate-y-0.5"
          >
            Contact Us
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>

      {/* Scroll-to-discover indicator */}
      <motion.button
        type="button"
        data-testid="about-scroll-indicator"
        onClick={() =>
          document
            .querySelector('[data-testid="about-our-story"]')
            ?.scrollIntoView({ behavior: "smooth" })
        }
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-liftile-cream/70 hover:text-liftile-orange transition-colors group"
        aria-label="Scroll to discover"
      >
        <span className="font-heading text-[10px] uppercase tracking-[0.32em]">
          Scroll to Discover
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="grid place-items-center w-9 h-9 rounded-full border border-current/30 group-hover:border-liftile-orange/70"
        >
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </motion.button>

      <style>{`
        @keyframes about-kenburns {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.10); }
        }
      `}</style>
    </section>
  );
}

/* =====================================================================
   OUR STORY — split-screen.
   Desktop: left ~45 % (sticky photo with parallax) / right ~55 % (copy).
   Mobile : stacked.
   ===================================================================== */
function OurStorySection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  const FEATURES = [
    { head: "5000+",   sub: "Sq. Ft. Manufacturing Facility" },
    { head: "26+",     sub: "Years of Industry Experience" },
    { head: "Premium", sub: "Branded Components" },
    { head: "Quality", sub: "Inspected Before Dispatch" },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="about-our-story"
      className="relative bg-liftile-cream py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-12 lg:gap-20 items-start">
          {/* --------- LEFT — sticky parallax photograph --------- */}
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative"
            >
              {/* Elegant orange accent square behind the photo */}
              <div
                aria-hidden
                className="absolute -bottom-6 -left-6 w-32 h-32 md:w-44 md:h-44 bg-liftile-orange/90 rounded-md hidden md:block"
              />
              <div
                aria-hidden
                className="absolute -top-6 -right-6 w-20 h-20 border-2 border-liftile-orange rounded-md hidden md:block"
              />

              <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden rounded-2xl shadow-[0_30px_70px_-25px_rgba(0,0,0,0.5)]">
                <motion.img
                  src={ABOUT_FACTORY_IMG}
                  alt="LIFTILE manufacturing facility, Vadodara"
                  loading="lazy"
                  style={{ y: photoY }}
                  className="absolute inset-0 w-full h-[115%] object-cover"
                />
                {/* Very subtle warm orange wash */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(160deg, rgba(255,107,26,0.0) 60%, rgba(255,107,26,0.12) 100%)",
                  }}
                />
              </div>

              {/* Tiny caption strip */}
              <div className="relative mt-5 flex items-center gap-3 text-liftile-matte-black/65">
                <span className="block w-8 h-px bg-liftile-orange" />
                <span className="font-heading text-[10px] uppercase tracking-[0.28em]">
                  Vadodara · Gujarat · India
                </span>
              </div>
            </motion.div>
          </div>

          {/* --------- RIGHT — narrative + features + quotation --------- */}
          <div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
            >
              <div className="heading-eyebrow mb-4 text-liftile-orange">
                About LIFTILE
              </div>
              <h2
                data-testid="about-story-heading"
                className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
              >
                Built on Experience.
                <br />
                <span className="text-liftile-orange">Driven by Innovation.</span>
              </h2>
            </motion.div>

            {/* Story paragraphs */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-9 space-y-5 font-body text-liftile-matte-black/75 text-base md:text-lg leading-relaxed max-w-2xl"
            >
              <p>
                Ekta Engineering was established in 2022 with a strong foundation
                in fabrication and engineering for construction equipment.
              </p>
              <p>
                During the early years, the company specialized in fabrication
                and assembly work for construction machinery, building valuable
                manufacturing expertise and engineering knowledge.
              </p>
              <p>
                In late 2023, we began a new chapter by focusing entirely on
                designing and manufacturing our own construction machinery. Our
                first Multi Functional Tower Hoist successfully completed
                manufacturing and testing in November 2023. Within days, we also
                developed our first Monkey Lift, marking the beginning of our
                own product line.
              </p>
              <p>
                In 2026, LIFTILE was officially introduced as our dedicated
                construction machinery brand, representing our commitment to
                premium engineering, uncompromising quality and customer-focused
                innovation.
              </p>
              <p>
                Today, every LIFTILE machine reflects the same philosophy that
                has guided us from the beginning:
                <span className="block mt-3 font-heading text-liftile-matte-black uppercase tracking-wide text-base md:text-lg">
                  Build machines we would proudly use ourselves.
                </span>
              </p>
            </motion.div>

            {/* Feature cards — staggered fade */}
            <div
              data-testid="about-feature-grid"
              className="grid grid-cols-2 gap-4 md:gap-5 mt-12 max-w-2xl"
            >
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.sub}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.12, duration: 0.55, ease: "easeOut" }}
                  data-testid={`about-feature-${i}`}
                  className="group relative bg-white rounded-xl p-6 md:p-7 border border-black/[0.04] shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)] hover:shadow-[0_18px_40px_-14px_rgba(255,107,26,0.35)] hover:-translate-y-0.5 transition-all duration-400"
                >
                  <div className="absolute top-0 left-6 w-10 h-0.5 bg-liftile-orange transition-all duration-400 group-hover:w-16" />
                  <div className="font-display text-3xl md:text-4xl text-liftile-orange leading-none">
                    {f.head}
                  </div>
                  <div className="mt-2 md:mt-3 font-heading text-[11px] md:text-xs uppercase tracking-wider text-liftile-matte-black/75 leading-snug">
                    {f.sub}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Premium quotation */}
            <motion.figure
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.15, duration: 0.7, ease: "easeOut" }}
              data-testid="about-quote"
              className="mt-14 md:mt-16 max-w-2xl relative pl-6 md:pl-8"
            >
              <span
                aria-hidden
                className="absolute left-0 top-1 bottom-1 w-[3px] bg-liftile-orange rounded"
              />
              <Quote
                aria-hidden
                className="absolute -top-2 left-6 md:left-8 w-7 h-7 text-liftile-orange/30 -translate-y-full"
              />
              <blockquote className="font-display italic text-xl md:text-2xl lg:text-[1.65rem] text-liftile-matte-black leading-snug tracking-tight">
                &ldquo;We believe every machine represents a customer&apos;s
                hard-earned investment. That is why we never compromise on
                quality, safety or engineering standards.&rdquo;
              </blockquote>
              <figcaption className="mt-4 font-heading text-[11px] uppercase tracking-[0.28em] text-liftile-matte-black/55">
                The LIFTILE Philosophy
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================== */
export default function AboutPage() {
  const seo = PAGE_SEO.about;
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "About Us", path: "/about" },
          ]),
        ]}
        jsonLdId="about"
      />
      <Navbar />
      <main>
        <AboutHero />
        <OurStorySection />
        <LeadershipSection />
        <ManufacturingExcellence />
        <InsideLiftileGallery />
        <QualityWithoutCompromise />
        <TrustedComponentsAbout />
        <EngineeringPhilosophy />
        <MissionVisionSection />
        <IndiaPresenceSection />
        <BuildingTomorrowSection />
        <LiftilePromiseSection />
        <AboutFAQSection />
      </main>

      <CTABanner
        headline="Experience the LIFTILE Difference."
        description="Discover our commitment to quality manufacturing, premium components and customer-focused engineering."
      />

      <Footer />
    </>
  );
}
